import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Gavel, ExternalLink, Search, Handshake, Scale, X as CloseIcon, Clock, AlertTriangle, ShieldAlert, Trash2, Loader2 } from "lucide-react";

// ⚠️ SAFETY SWITCH — set to true to expose the "Reset platform" danger button.
// Ask your agent to flip this to `true` when you need to nuke test data again.
const SHOW_RESET_BUTTON = false;

const fmt = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function StatusPill({ status }) {
  const cls = status === "admin_review"
    ? "bg-nexus-pink/15 text-nexus-pink border-nexus-pink/40"
    : "bg-yellow-500/10 text-yellow-300 border-yellow-500/30";
  return <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`} data-testid={`dispute-status-${status}`}>{status.replace(/_/g, " ")}</span>;
}

function timeSince(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function ResolveDialog({ hire, onClose, onResolved }) {
  const [outcome, setOutcome] = useState("release_to_creator");
  const [pct, setPct] = useState(50);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!hire) return;
    api.get(`/admin/disputes/${hire.hire_id}`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [hire]);

  if (!hire) return null;
  const amount = hire.amount || 0;
  const creatorFee = amount * (outcome === "split" ? pct / 100 : outcome === "release_to_creator" ? 1 : 0) * 0.12;
  const creatorGets = outcome === "release_to_creator" ? amount - amount * 0.12
                     : outcome === "refund_brand" ? 0
                     : (amount * pct / 100) - creatorFee;
  const brandRefund = outcome === "refund_brand" ? (hire.brand_total || amount)
                     : outcome === "release_to_creator" ? 0
                     : amount * (100 - pct) / 100;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/disputes/${hire.hire_id}/resolve`, {
        outcome,
        creator_percent: pct,
        admin_note: note.trim(),
      });
      toast.success("Dispute resolved");
      onResolved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass rounded-3xl border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="resolve-dialog">
        <div className="p-6 border-b border-white/5 flex items-center gap-3">
          <Scale className="h-5 w-5 text-nexus-pink" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-nexus-pink">Resolve dispute</div>
            <div className="font-display font-bold text-lg truncate">{hire.package_title || "Hire"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="resolve-close-btn"><CloseIcon className="h-5 w-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 rounded-xl bg-white/[0.02] hairline">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Brand</div>
              <div className="font-semibold truncate">{hire.brand.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{hire.brand.email}</div>
            </div>
            <div className="p-3 rounded-xl bg-white/[0.02] hairline">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Creator</div>
              <div className="font-semibold truncate">{hire.creator.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">{hire.creator.email}</div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-white/[0.02] hairline text-sm space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Dispute opened by <span className="text-zinc-300">{hire.dispute_opened_by_role}</span> · {timeSince(hire.dispute_opened_at)}</div>
            {hire.dispute_reason && <p className="text-zinc-200"><span className="text-zinc-500">Reason:</span> {hire.dispute_reason}</p>}
            {hire.deliverable_url && (
              <a href={hire.deliverable_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-nexus-blue hover:underline">
                <ExternalLink className="h-3 w-3" /> View delivery
              </a>
            )}
            {hire.delivery_note && <p className="text-xs text-zinc-400 whitespace-pre-wrap"><span className="text-zinc-500">Delivery note:</span> {hire.delivery_note}</p>}
            {hire.revision_count > 0 && <div className="text-[10px] text-yellow-300/80">{hire.revision_count} revision(s) requested</div>}
          </div>

          {detail?.evidence?.length > 0 && (
            <div className="p-4 rounded-xl bg-white/[0.02] hairline space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Evidence timeline · {detail.evidence.length} entries</div>
              <ol className="space-y-2 max-h-56 overflow-y-auto" data-testid="admin-evidence-list">
                {detail.evidence.map((e) => (
                  <li key={e.id} className="p-2.5 rounded-lg bg-black/30 border border-white/5">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs">
                        <span className={`font-semibold ${e.author_role === "brand" ? "text-nexus-blue" : "text-nexus-pink"}`}>{e.author_name}</span>{" "}
                        <span className="text-[10px] uppercase tracking-widest text-zinc-500">{e.author_role}</span>
                      </div>
                      <div className="text-[10px] text-zinc-600">{new Date(e.created_at).toLocaleString("en-GB")}</div>
                    </div>
                    <p className="text-xs text-zinc-200 whitespace-pre-wrap">{e.note}</p>
                    {e.url && (
                      <a href={e.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-[11px] text-nexus-blue hover:underline">
                        <ExternalLink className="h-3 w-3" /> {e.url.length > 60 ? e.url.slice(0, 60) + "…" : e.url}
                      </a>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Outcome</div>
            <div className="grid grid-cols-3 gap-2" data-testid="outcome-radio-group">
              {[
                { v: "release_to_creator", label: "Full to creator" },
                { v: "refund_brand", label: "Full refund" },
                { v: "split", label: "Split" },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setOutcome(o.v)}
                  data-testid={`outcome-${o.v}`}
                  className={`p-3 rounded-xl text-xs font-semibold transition-all ${outcome === o.v ? "bg-nexus-pink/15 border-nexus-pink text-white border" : "bg-white/[0.02] border border-white/10 text-zinc-400 hover:text-white"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {outcome === "split" && (
              <div className="space-y-2 pt-2">
                <div className="flex items-center gap-3">
                  <input type="range" min="0" max="100" step="5" value={pct} onChange={(e) => setPct(parseInt(e.target.value))} className="flex-1 accent-nexus-pink" data-testid="split-range" />
                  <div className="font-display font-bold text-lg tabular-nums w-14 text-right nexus-gradient-text">{pct}%</div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 text-xs pt-2">
              <div className="p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-emerald-200">
                Creator gets: <b className="tabular-nums" data-testid="preview-creator">{fmt(creatorGets)}</b>
              </div>
              <div className="p-2.5 rounded-lg bg-purple-500/5 border border-purple-500/20 text-purple-200">
                Brand refund: <b className="tabular-nums" data-testid="preview-refund">{fmt(brandRefund)}</b>
              </div>
            </div>

            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Admin note (shared with both parties in the dispute record)…"
              className="bg-white/[0.02] border-white/10 text-white min-h-[80px]"
              data-testid="admin-note-input"
            />

            <Button onClick={submit} disabled={busy} className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 w-full h-11" data-testid="resolve-submit-btn">
              <Gavel className="h-4 w-4 mr-2" /> Resolve dispute
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DangerZone({ onWiped }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    if (typed !== "WIPE") return;
    setBusy(true);
    try {
      const r = await api.post("/admin/wipe-test-accounts?confirm=WIPE-NEXUS");
      setReport(r.data);
      toast.success("Platform reset — all non-admin data cleared");
      if (onWiped) onWiped();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const close = () => { setOpen(false); setTyped(""); setReport(null); };

  return (
    <>
      <div className="glass rounded-2xl p-6 border border-red-500/20 bg-red-500/[0.02]" data-testid="danger-zone">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30">
            <ShieldAlert className="h-5 w-5 text-red-400" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="text-[10px] uppercase tracking-widest text-red-400 mb-1">Danger zone</div>
            <div className="font-display font-bold text-lg">Reset the platform</div>
            <p className="text-sm text-zinc-400 mt-1 max-w-xl">
              Deletes every non-owner account and all their hires, campaigns, messages, reviews, case studies, XP and subscriptions.
              Owner accounts and the seeded SEO blog posts are preserved. Irreversible.
            </p>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500/25 hover:text-red-200 rounded-full h-10 px-5"
            data-testid="danger-zone-open-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" /> Reset platform…
          </Button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
          <div className="glass rounded-3xl border border-red-500/20 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="reset-dialog">
            <div className="p-6 border-b border-white/5 flex items-center gap-3">
              <ShieldAlert className="h-5 w-5 text-red-400" />
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-red-400">Confirm platform reset</div>
                <div className="font-display font-bold text-lg">This cannot be undone</div>
              </div>
              <button onClick={close} className="text-zinc-400 hover:text-white" data-testid="reset-close-btn"><CloseIcon className="h-5 w-5" /></button>
            </div>

            {!report ? (
              <div className="p-6 space-y-4">
                <ul className="text-sm text-zinc-300 space-y-1.5 list-disc list-inside">
                  <li><b>Deletes:</b> every creator + brand account (except platform owners)</li>
                  <li><b>Deletes:</b> hires, campaigns, applications, messages, reviews, case studies</li>
                  <li><b>Deletes:</b> XP events, subscriptions, saved creators, uploads</li>
                  <li><b>Preserves:</b> owner accounts (info@ + charles-armstrong@) and blog posts</li>
                </ul>
                <div className="p-3 rounded-lg bg-red-500/[0.06] border border-red-500/20 text-xs text-red-200">
                  Type <span className="font-mono font-bold text-red-300">WIPE</span> to enable the button.
                </div>
                <Input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value.toUpperCase())}
                  placeholder="Type WIPE to confirm"
                  className="bg-white/[0.02] border-white/10 text-white font-mono uppercase tracking-widest text-center"
                  data-testid="reset-confirm-input"
                  autoFocus
                />
                <Button
                  onClick={run}
                  disabled={typed !== "WIPE" || busy}
                  className="w-full h-11 rounded-full bg-red-500 text-white hover:bg-red-600 font-semibold disabled:bg-red-500/20 disabled:text-red-300/50"
                  data-testid="reset-execute-btn"
                >
                  {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…</> : <><Trash2 className="h-4 w-4 mr-2" /> Reset the platform</>}
                </Button>
              </div>
            ) : (
              <div className="p-6 space-y-4" data-testid="reset-report">
                <div className="text-sm text-emerald-300 font-semibold flex items-center gap-2">
                  ✅ Platform reset complete
                </div>
                <div className="text-xs text-zinc-400">Kept {report.report.users_kept} owner account(s): {report.report.kept_emails.join(", ")}</div>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto text-xs">
                  {Object.entries(report.report).filter(([k]) => !["users_kept", "kept_emails"].includes(k)).map(([k, v]) => (
                    <div key={k} className="flex justify-between p-2 rounded bg-white/[0.02] hairline">
                      <span className="text-zinc-500">{k}</span>
                      <span className="tabular-nums text-white font-semibold">{v}</span>
                    </div>
                  ))}
                </div>
                <Button onClick={close} className="w-full h-11 rounded-full nexus-gradient text-white border-0 font-semibold" data-testid="reset-done-btn">
                  Done
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function AdminDisputes() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/disputes");
      setData(r.data);
    } catch (e) {
      const code = e.response?.status;
      if (code === 403) { toast.error("Admin only"); nav("/"); return; }
      if (code === 401) { toast.error("Log in first"); nav("/auth?mode=login"); return; }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <BrandShell title="Admin · Disputes"><div className="p-10 text-zinc-500 animate-pulse">Loading…</div></BrandShell>;

  const t = data.totals || {};
  let rows = data.rows || [];
  if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
  if (filter.trim()) {
    const s = filter.toLowerCase();
    rows = rows.filter((r) =>
      [r.brand.name, r.creator.name, r.brand.email, r.creator.email, r.package_title, r.hire_id, r.dispute_reason]
        .some((v) => (v || "").toLowerCase().includes(s))
    );
  }

  return (
    <BrandShell title="Admin · Disputes">
      <SEO title="Admin · Disputes · Nexus" description="Review and resolve open disputes." path="/admin/disputes" />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <Gavel className="h-3.5 w-3.5 text-nexus-pink" /> Admin only
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tighter">Dispute review</h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Every open dispute across the platform. Ruling releases, refunds or splits the escrow instantly and notifies both parties.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "Open (party-to-party)", v: t.open || 0, tone: "warn" },
            { l: "Escalated to admin", v: t.admin_review || 0, tone: "pink" },
            { l: "Total open", v: t.count || 0 },
            { l: "Oldest", v: rows[rows.length - 1]?.dispute_opened_at ? timeSince(rows[rows.length - 1].dispute_opened_at) : "—" },
          ].map((k, i) => (
            <div key={i} className="glass rounded-2xl p-4" data-testid={`dispute-stat-${i}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k.l}</div>
              <div className={`font-display font-bold text-2xl mt-1 tabular-nums ${k.tone === "warn" ? "text-yellow-300" : k.tone === "pink" ? "text-nexus-pink" : "text-white"}`}>{k.v}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search brand, creator, reason…" className="pl-10 bg-white/[0.02] border-white/10 text-white" data-testid="dispute-filter-input" />
          </div>
          <div className="flex gap-1" data-testid="dispute-status-tabs">
            {[["all", "All"], ["disputed", "Open"], ["admin_review", "Escalated"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setStatusFilter(v)}
                data-testid={`dispute-tab-${v}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${statusFilter === v ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="glass rounded-2xl p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                <tr>
                  <th className="text-left p-3">Opened</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Brand</th>
                  <th className="text-left p-3">Creator</th>
                  <th className="text-left p-3">Package</th>
                  <th className="text-right p-3">Escrow £</th>
                  <th className="text-center p-3">Evidence</th>
                  <th className="text-right p-3"></th>
                </tr>
              </thead>
              <tbody data-testid="admin-disputes-table">
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-zinc-500 text-xs">🎉 No open disputes.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.hire_id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`dispute-row-${r.hire_id}`}>
                    <td className="p-3 text-[11px] text-zinc-400 flex items-center gap-1">
                      <Clock className="h-3 w-3 text-zinc-600" />
                      {timeSince(r.dispute_opened_at)}
                    </td>
                    <td className="p-3"><StatusPill status={r.status} /></td>
                    <td className="p-3">
                      <div className="font-semibold truncate max-w-[160px]">{r.brand.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[160px]">{r.brand.email}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-semibold truncate max-w-[160px]">{r.creator.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[160px]">{r.creator.email}</div>
                    </td>
                    <td className="p-3 truncate max-w-[200px]">
                      <div className="truncate">{r.package_title || "—"}</div>
                      {r.dispute_opened_by_role && (
                        <div className="text-[10px] text-zinc-500 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> raised by {r.dispute_opened_by_role}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right tabular-nums text-nexus-blue">{fmt(r.amount)}</td>
                    <td className="p-3 text-center text-xs text-zinc-300">{r.evidence_count}</td>
                    <td className="p-3 text-right">
                      <Button
                        onClick={() => setSelected(r)}
                        className="nexus-gradient text-white rounded-full border-0 font-semibold hover:opacity-90 h-8 px-3 text-xs"
                        data-testid={`dispute-review-btn-${r.hire_id}`}
                      >
                        <Handshake className="h-3.5 w-3.5 mr-1.5" /> Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-zinc-500">Generated at {new Date(data.generated_at).toLocaleString("en-GB")}. Only visible to accounts in ADMIN_EMAILS.</p>

        {SHOW_RESET_BUTTON && <DangerZone onWiped={load} />}
      </div>

      {selected && <ResolveDialog hire={selected} onClose={() => setSelected(null)} onResolved={load} />}
    </BrandShell>
  );
}
